import {
  AboveValueConfigKind,
  BelowValueConfigKind,
  BetweenValueConfigKind,
  CohortFilterGranularity,
  InTheLastValueConfigKind,
  UserInteractionsValueConfig,
} from '@/api/generated/model';
import { useState } from 'react';
import { FilterWhenFields, IFilterWhenFields, whenFieldsToConfig } from './filter-when-forms';
import { translate } from '@/utils/translations';
import { nullableNumberToString } from '@/utils/numbers.util';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/comps/primitives/select';
import { Button } from '@/comps/primitives/button';
import { Input } from '@/comps/primitives/input';
type ValueKind = BetweenValueConfigKind | AboveValueConfigKind | BelowValueConfigKind;

type IProps = {
  onApply: (e: UserInteractionsValueConfig) => void;
};

export const NumberOfInteractionsForm = ({ onApply }: IProps) => {
  const [valueKind, setValueKind] = useState<ValueKind>('above');
  const [firstNumber, setFirstNumber] = useState<number>(10);
  const [secondNumber, setSecondNumber] = useState<number>(10);
  const [filterWhenFields, setFilterWhenFields] = useState<IFilterWhenFields>({
    granularity: CohortFilterGranularity.day,
    kind: InTheLastValueConfigKind.in_the_last,
    number: 1,
    pickedDate: null,
    pickedDates: null,
    filter: null,
  });
  const [per, setPer] = useState<CohortFilterGranularity | null>(null);

  const whenConfig = whenFieldsToConfig(filterWhenFields);

  const apply = () => {
    if (!whenConfig) return;

    if (valueKind === AboveValueConfigKind.above || valueKind === BelowValueConfigKind.below) {
      onApply({
        interactions_count: {
          kind: valueKind,
          value: firstNumber,
          per: per || undefined,
          offset: new Date().getTimezoneOffset(),
        },
        dates: whenConfig as UserInteractionsValueConfig['dates'],
      });
    } else if (valueKind === BetweenValueConfigKind.between) {
      onApply({
        interactions_count: {
          kind: 'between',
          value: [firstNumber, secondNumber],
          per: per || undefined,
          offset: new Date().getTimezoneOffset(),
        },
        dates: whenConfig as UserInteractionsValueConfig['dates'],
      });
    }
  };

  const handleSelect = (value: string) => {
    if (value === 'null') {
      setPer(null);
    } else {
      setPer(value as CohortFilterGranularity);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm">Users who have</p>
      <div className="flex items-center gap-1">
        <Select value={valueKind} onValueChange={(value) => setValueKind(value as ValueKind)}>
          <SelectTrigger className="w-auto">
            <SelectValue>{translate('value_config_kind', valueKind)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AboveValueConfigKind.above}>
              {translate('value_config_kind', AboveValueConfigKind.above)}
            </SelectItem>
            <SelectItem value={BelowValueConfigKind.below}>
              {translate('value_config_kind', BelowValueConfigKind.below)}
            </SelectItem>
            <SelectItem value={BetweenValueConfigKind.between}>
              {translate('value_config_kind', BetweenValueConfigKind.between)}
            </SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={nullableNumberToString(firstNumber)}
          type="number"
          className="h-8 w-10 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          onChange={(e) => setFirstNumber(parseInt(e.target.value))}
        />
        {valueKind === BetweenValueConfigKind.between && (
          <>
            <p className="text-sm">and</p>
            <Input
              value={nullableNumberToString(secondNumber)}
              type="number"
              className="h-8 w-10 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              onChange={(e) => setSecondNumber(parseInt(e.target.value))}
            />
          </>
        )}
      </div>

      <div className="flex items-center gap-1">
        <p className="text-sm">Interactions</p>
        <Select value={per || ''} onValueChange={handleSelect}>
          <SelectTrigger className="">
            <SelectValue placeholder="+ Select frequency">{per ? `per ${per}` : '+ Select frequency'}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CohortFilterGranularity.day}>per day</SelectItem>
            <SelectItem value={CohortFilterGranularity.week}>per week</SelectItem>
            <SelectItem value={CohortFilterGranularity.month}>per month</SelectItem>
            <SelectItem value={'null'}>Remove filter</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <FilterWhenFields
        disableSpecialFilters={true}
        onChange={setFilterWhenFields}
        initialValues={filterWhenFields}
      ></FilterWhenFields>
      <Button className="mt-2" size="sm" onClick={apply} disabled={!whenConfig}>
        Apply
      </Button>
    </div>
  );
};
